/**
 * 全新原生滾動商品圖庫
 * 完全不使用 Slick Carousel
 * 實現 Instagram Reels 般的即時滾動體驗
 */

class NativeProductGallery {
    constructor(container) {
        this.container = container;
        this.mainItems = container.querySelectorAll('.native-gallery-main-item');
        this.thumbnails = container.querySelectorAll('.native-gallery-thumbnail');
        this.thumbnailsWrapper = container.querySelector('.native-gallery-thumbnails-wrapper');
        this.thumbnailsTrack = container.querySelector('.native-gallery-thumbnails-track');
        this.prevArrow = container.querySelector('.native-gallery-arrow-prev');
        this.nextArrow = container.querySelector('.native-gallery-arrow-next');
        this.counterCurrent = container.querySelector('.native-gallery-counter-current');
        this.counterTotal = container.querySelector('.native-gallery-counter-total');

        this.currentIndex = 0;
        this.isTracking = false;
        this.rafId = null;
        this.scrollTimeout = null;
        this.trackingDisableTimeout = null;

        // 拖動滾動相關
        this.isDragging = false;
        this.startX = 0;
        this.startY = 0;
        this.scrollLeft = 0;
        this.scrollTop = 0;

        this.init();
    }
    
    init() {
        if (!this.thumbnailsWrapper || this.thumbnails.length === 0) return;

        // 移除拖動內容功能 - 只保留滾動條
        // this.setupDragScroll();

        // 設置即時滾動追蹤
        this.setupRealtimeScrollTracking();

        // 恢復縮圖點擊功能
        this.setupThumbnailClick();

        // 設置箭頭導航
        this.setupArrowNavigation();

        console.log('✅ Native Product Gallery initialized');
    }
    
    setupDragScroll() {
        // 滑鼠按下開始拖動
        this.thumbnailsWrapper.addEventListener('mousedown', (e) => {
            this.isDragging = true;
            this.thumbnailsWrapper.style.cursor = 'grabbing';
            this.thumbnailsWrapper.style.userSelect = 'none';

            this.startX = e.pageX - this.thumbnailsWrapper.offsetLeft;
            this.startY = e.pageY - this.thumbnailsWrapper.offsetTop;
            this.scrollLeft = this.thumbnailsWrapper.scrollLeft;
            this.scrollTop = this.thumbnailsWrapper.scrollTop;
        });

        // 滑鼠移動時滾動
        this.thumbnailsWrapper.addEventListener('mousemove', (e) => {
            if (!this.isDragging) return;
            e.preventDefault();

            const x = e.pageX - this.thumbnailsWrapper.offsetLeft;
            const y = e.pageY - this.thumbnailsWrapper.offsetTop;
            const walkX = (x - this.startX) * 2; // 滾動速度倍數
            const walkY = (y - this.startY) * 2;

            // 根據佈局決定滾動方向
            const layout = this.container.dataset.layout;
            if (layout === '1' || layout === '2') {
                // 垂直佈局
                this.thumbnailsWrapper.scrollTop = this.scrollTop - walkY;
            } else {
                // 水平佈局
                this.thumbnailsWrapper.scrollLeft = this.scrollLeft - walkX;
            }
        });

        // 滑鼠放開停止拖動
        const stopDragging = () => {
            this.isDragging = false;
            this.thumbnailsWrapper.style.cursor = 'grab';
            this.thumbnailsWrapper.style.userSelect = '';
        };

        this.thumbnailsWrapper.addEventListener('mouseup', stopDragging);
        this.thumbnailsWrapper.addEventListener('mouseleave', stopDragging);

        // 設置游標樣式
        this.thumbnailsWrapper.style.cursor = 'grab';

        // 觸控設備原生支持拖動滾動，不需要額外處理
    }

    setupRealtimeScrollTracking() {
        const startTracking = () => {
            // 如果追蹤被暫時禁用，不啟動
            if (this.trackingDisableTimeout) return;
            if (this.isTracking) return;
            this.isTracking = true;
            this.trackScrollPosition();
        };
        
        const stopTracking = () => {
            this.isTracking = false;
            if (this.rafId) {
                cancelAnimationFrame(this.rafId);
                this.rafId = null;
            }
        };
        
        // 滾動時開始追蹤
        this.thumbnailsWrapper.addEventListener('scroll', () => {
            startTracking();
            
            // 滾動停止後停止追蹤（節省性能）
            clearTimeout(this.scrollTimeout);
            this.scrollTimeout = setTimeout(() => {
                stopTracking();
            }, 150);
        }, { passive: true });
        
        // 觸控/拖動時開始追蹤
        this.thumbnailsWrapper.addEventListener('touchstart', startTracking, { passive: true });
        this.thumbnailsWrapper.addEventListener('mousedown', startTracking);
        
        // 觸控/拖動結束時延遲停止追蹤
        this.thumbnailsWrapper.addEventListener('touchend', () => {
            setTimeout(stopTracking, 150);
        });
        this.thumbnailsWrapper.addEventListener('mouseup', () => {
            setTimeout(stopTracking, 150);
        });
    }
    
    trackScrollPosition() {
        if (!this.isTracking) return;

        const layout = this.container.dataset.layout;
        const isVertical = layout === '1' || layout === '2';

        const wrapperRect = this.thumbnailsWrapper.getBoundingClientRect();

        // 收集所有可見的縮圖及其可見度
        const visibleThumbs = [];

        this.thumbnails.forEach((thumb) => {
            const thumbRect = thumb.getBoundingClientRect();

            // 計算縮圖在可見區域內的比例
            let visibleRatio = 0;

            if (isVertical) {
                // 垂直佈局
                const thumbTop = thumbRect.top;
                const thumbBottom = thumbRect.bottom;
                const wrapperTop = wrapperRect.top;
                const wrapperBottom = wrapperRect.bottom;

                const visibleTop = Math.max(thumbTop, wrapperTop);
                const visibleBottom = Math.min(thumbBottom, wrapperBottom);
                const visibleHeight = Math.max(0, visibleBottom - visibleTop);

                visibleRatio = visibleHeight / thumbRect.height;
            } else {
                // 水平佈局
                const thumbLeft = thumbRect.left;
                const thumbRight = thumbRect.right;
                const wrapperLeft = wrapperRect.left;
                const wrapperRight = wrapperRect.right;

                const visibleLeft = Math.max(thumbLeft, wrapperLeft);
                const visibleRight = Math.min(thumbRight, wrapperRight);
                const visibleWidth = Math.max(0, visibleRight - visibleLeft);

                visibleRatio = visibleWidth / thumbRect.width;
            }

            // 只考慮至少 10% 可見的縮圖
            if (visibleRatio > 0.1) {
                visibleThumbs.push({
                    thumb: thumb,
                    mediaIndex: parseInt(thumb.dataset.mediaIndex),
                    visibleRatio: visibleRatio
                });
            }
        });

        if (visibleThumbs.length === 0) {
            this.rafId = requestAnimationFrame(() => this.trackScrollPosition());
            return;
        }

        // 找到可見度最高的縮圖
        visibleThumbs.sort((a, b) => b.visibleRatio - a.visibleRatio);
        const bestThumb = visibleThumbs[0];

        // 更新主圖（使用 media index 而不是數組索引）
        if (bestThumb.mediaIndex !== this.currentIndex) {
            this.goToSlideByMediaIndex(bestThumb.mediaIndex);
        }

        // 繼續追蹤（60fps）
        this.rafId = requestAnimationFrame(() => this.trackScrollPosition());
    }
    
    setupThumbnailClick() {
        this.thumbnails.forEach((thumb) => {
            thumb.addEventListener('click', (e) => {
                e.preventDefault();
                const mediaIndex = parseInt(thumb.dataset.mediaIndex);

                // 暫時禁用滾動追蹤，避免自動切換回去
                this.disableTrackingTemporarily();

                this.goToSlideByMediaIndex(mediaIndex);

                // 滾動到點擊的縮圖（居中）
                thumb.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'center'
                });
            });
        });
    }
    
    setupArrowNavigation() {
        if (this.prevArrow) {
            this.prevArrow.addEventListener('click', () => {
                // 暫時禁用滾動追蹤，避免自動切換回去
                this.disableTrackingTemporarily();

                const newMediaIndex = this.currentIndex > 0 ? this.currentIndex - 1 : this.mainItems.length - 1;
                this.goToSlideByMediaIndex(newMediaIndex);
                this.scrollThumbnailIntoViewByMediaIndex(newMediaIndex);
            });
        }

        if (this.nextArrow) {
            this.nextArrow.addEventListener('click', () => {
                // 暫時禁用滾動追蹤，避免自動切換回去
                this.disableTrackingTemporarily();

                const newMediaIndex = this.currentIndex < this.mainItems.length - 1 ? this.currentIndex + 1 : 0;
                this.goToSlideByMediaIndex(newMediaIndex);
                this.scrollThumbnailIntoViewByMediaIndex(newMediaIndex);
            });
        }
    }
    
    goToSlideByMediaIndex(mediaIndex) {
        if (mediaIndex === this.currentIndex) return;

        // 更新主圖（使用 data-media-index 匹配）
        this.mainItems.forEach((item) => {
            const itemMediaIndex = parseInt(item.dataset.mediaIndex);
            if (itemMediaIndex === mediaIndex) {
                item.classList.add('active');
            } else {
                item.classList.remove('active');
            }
        });

        // 更新縮圖狀態（使用 data-media-index 匹配）
        this.thumbnails.forEach((thumb) => {
            const thumbMediaIndex = parseInt(thumb.dataset.mediaIndex);
            if (thumbMediaIndex === mediaIndex) {
                thumb.classList.add('active');
            } else {
                thumb.classList.remove('active');
            }
        });

        // 更新計數器
        if (this.counterCurrent) {
            this.counterCurrent.textContent = mediaIndex + 1;
        }

        this.currentIndex = mediaIndex;
    }

    // 保留舊的 goToSlide 函數以防其他地方使用
    goToSlide(index) {
        this.goToSlideByMediaIndex(index);
    }
    
    scrollThumbnailIntoViewByMediaIndex(mediaIndex) {
        // 找到對應 media index 的縮圖
        const thumb = Array.from(this.thumbnails).find(t => parseInt(t.dataset.mediaIndex) === mediaIndex);
        if (thumb) {
            thumb.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center'
            });
        }
    }

    // 保留舊的函數以防其他地方使用
    scrollThumbnailIntoView(index) {
        this.scrollThumbnailIntoViewByMediaIndex(index);
    }

    // 暫時禁用滾動追蹤（用於點擊縮圖時）
    disableTrackingTemporarily() {
        // 停止當前的追蹤
        this.isTracking = false;
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }

        // 清除現有的超時
        if (this.trackingDisableTimeout) {
            clearTimeout(this.trackingDisableTimeout);
        }

        // 2 秒後重新啟用追蹤
        this.trackingDisableTimeout = setTimeout(() => {
            this.trackingDisableTimeout = null;
        }, 2000);
    }
}

// 初始化所有原生圖庫
function initNativeGalleries() {
    const galleries = document.querySelectorAll('[data-native-gallery]');
    
    galleries.forEach(gallery => {
        if (!gallery.dataset.initialized) {
            new NativeProductGallery(gallery);
            gallery.dataset.initialized = 'true';
        }
    });
}

// DOM 載入完成後初始化
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initNativeGalleries);
} else {
    initNativeGalleries();
}

// 監聽動態內容載入（例如 Quick View）
const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) { // Element node
                if (node.matches('[data-native-gallery]')) {
                    new NativeProductGallery(node);
                    node.dataset.initialized = 'true';
                } else {
                    const galleries = node.querySelectorAll('[data-native-gallery]');
                    galleries.forEach(gallery => {
                        if (!gallery.dataset.initialized) {
                            new NativeProductGallery(gallery);
                            gallery.dataset.initialized = 'true';
                        }
                    });
                }
            }
        });
    });
});

observer.observe(document.body, {
    childList: true,
    subtree: true
});

// 導出供外部使用
window.NativeProductGallery = NativeProductGallery;
window.initNativeGalleries = initNativeGalleries;

console.log('✅ Native Product Gallery script loaded');

